import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './contact.scss'; // Import SCSS styles
import useFormFocusHandlers from '../../hooks/useFormFocusHandlers';

function ContactUs() {
    console.log("ContactUs component is rendering");
      // Reference for the textarea element
    const textareaRef = useRef(null);
    
    // Reference for the first name field to enable auto-focus
    const firstNameRef = useRef(null);
    
    // Use our custom hook for form focus handling
    const { 
        handleFocus, 
        handleBlur, 
        handleChange, 
        handleTextareaChange,
        handleFormContainerClick
    } = useFormFocusHandlers(textareaRef);    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        subject: 'General Inquiry',
        message: ''
    });

    // Form validation state
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    // Validation rules
    const validateField = (name, value) => {
        const errors = {};

        switch (name) {
            case 'firstName':
                if (!value.trim()) {
                    errors.firstName = 'First name is required';
                } else if (value.trim().length < 2) {
                    errors.firstName = 'First name must be at least 2 characters';
                } else if (!/^[a-zA-Z\s'-]+$/.test(value)) {
                    errors.firstName = 'First name can only contain letters, spaces, hyphens, and apostrophes';
                }
                break;

            case 'lastName':
                if (!value.trim()) {
                    errors.lastName = 'Last name is required';
                } else if (value.trim().length < 2) {
                    errors.lastName = 'Last name must be at least 2 characters';
                } else if (!/^[a-zA-Z\s'-]+$/.test(value)) {
                    errors.lastName = 'Last name can only contain letters, spaces, hyphens, and apostrophes';
                }
                break;

            case 'email':
                if (!value.trim()) {
                    errors.email = 'Email address is required';
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    errors.email = 'Please enter a valid email address';
                }
                break;

            case 'phone':
                // Phone is optional, but if provided, should be valid
                if (value.trim() && !/^[\+]?[\d\s\-\(\)]{10,}$/.test(value.replace(/\s/g, ''))) {
                    errors.phone = 'Please enter a valid phone number';
                }
                break;

            case 'company':
                // Company is optional, no validation needed
                break;

            case 'message':
                if (!value.trim()) {
                    errors.message = 'Message is required';
                } else if (value.trim().length < 10) {
                    errors.message = 'Message must be at least 10 characters';
                } else if (value.trim().length > 1000) {
                    errors.message = 'Message must be less than 1000 characters';
                }
                break;

            default:
                break;
        }

        return errors;
    };

    // Validate all fields
    const validateForm = () => {
        const allErrors = {};
        Object.keys(formData).forEach(field => {
            const fieldErrors = validateField(field, formData[field]);
            Object.assign(allErrors, fieldErrors);
        });
        return allErrors;
    };

    // Enhanced change handler with validation
    const handleChangeWithValidation = (e) => {
        const { name, value } = e.target;
        
        // Update form data
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Mark field as touched
        setTouched(prev => ({
            ...prev,
            [name]: true
        }));

        // Validate the field and update errors
        const fieldErrors = validateField(name, value);
        setErrors(prev => ({
            ...prev,
            ...fieldErrors,
            // Clear error if field is now valid
            [name]: fieldErrors[name] || undefined
        }));

        // Call original change handler for scroll management
        handleChange(e, setFormData);
    };

    // Enhanced blur handler with validation
    const handleBlurWithValidation = (e) => {
        const { name, value } = e.target;
        
        // Mark field as touched
        setTouched(prev => ({
            ...prev,
            [name]: true
        }));

        // Validate the field
        const fieldErrors = validateField(name, value);
        setErrors(prev => ({
            ...prev,
            ...fieldErrors,
            [name]: fieldErrors[name] || undefined
        }));

        // Call original blur handler
        handleBlur(e);
    };

    // Function to handle setting the subject from URL hash
    const setSubjectFromHash = () => {
        // Check for hash parameters to set the subject
        const hashString = window.location.hash.substring(1); // Remove the # character
        console.log("Hash string:", hashString);
        
        // Parse hash manually to handle direct parameters without ?
        let subjectParam = null;
        if (hashString.includes("subject=")) {
            subjectParam = hashString.split("subject=")[1];
            // Remove any additional params
            if (subjectParam.includes("&")) {
                subjectParam = subjectParam.split("&")[0];
            }
        }
        
        console.log("Subject parameter:", subjectParam);
        
        // Set the subject if it exists in hash params
        if (subjectParam) {
            // Map URL parameters to dropdown options
            const subjectMapping = {
                'training': 'Training',
                'consulting': 'Consulting',
                'consultancy': 'Consulting',
                'edtech': 'EdTech Solutions'
            };
            
            const mappedSubject = subjectMapping[subjectParam.toLowerCase()];
            console.log("Mapped subject:", mappedSubject);
            
            if (mappedSubject) {
                setFormData(prev => ({
                    ...prev,
                    subject: mappedSubject
                }));
                console.log("Subject set to:", mappedSubject);
            }
        }
    };    // Run on component mount
    useEffect(() => {
        console.log("ContactUs component mounted");
        document.querySelector(".contact-container") && 
            console.log("Found contact-container in DOM");
            
        // Set initial subject from hash
        setSubjectFromHash();
        
        // Determine if we should auto-focus based on how user arrived
        const shouldAutoFocus = () => {
            const currentPath = window.location.pathname;
            const hasHashParams = window.location.hash.includes("subject=");
            
            // Auto-focus if:
            // 1. User is on dedicated contact page (/contact)
            // 2. User came with hash parameters (from EdTech/Consulting)
            // 3. User came from a direct contact link (referrer contains contact-related terms)
            return currentPath === '/contact' || 
                   hasHashParams ||
                   (document.referrer && (
                       document.referrer.includes('/edtech') || 
                       document.referrer.includes('/consulting') ||
                       document.referrer.includes('contact')
                   ));
        };
        
        // Auto-focus the first name field only when appropriate
        if (shouldAutoFocus()) {
            const focusTimer = setTimeout(() => {
                if (firstNameRef.current) {
                    firstNameRef.current.focus();
                    console.log("Auto-focused first name field - user intended to use contact form");
                }
            }, 100);
            
            // Store timer for cleanup
            return () => clearTimeout(focusTimer);
        } else {
            console.log("Skipping auto-focus - user likely just browsing homepage");
        }
        
        // Add event listener for hash changes
        const handleHashChange = () => {
            setSubjectFromHash();
            // Also auto-focus on hash changes since they indicate intent to use form
            if (firstNameRef.current && window.location.hash.includes("subject=")) {
                firstNameRef.current.focus();
                console.log("Auto-focused on hash change - user clicked subject-specific link");
            }
        };
        
        window.addEventListener('hashchange', handleHashChange);
        
        // Cleanup
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, []);

    const [showThankYou, setShowThankYou] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Honeypot field for spam detection (hidden from users)
    const [honeypot, setHoneypot] = useState('');
    
    // Track form interaction time (humans take time to fill forms)
    const [formStartTime] = useState(Date.now());    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Prevent double submission
        if (isSubmitting) {
            return;
        }
        
        // Honeypot check - if filled, it's likely a bot
        if (honeypot.trim() !== '') {
            console.log('Honeypot triggered - potential spam');
            return;
        }
        
        // Time-based check - humans need at least 3 seconds to fill a form
        const timeTaken = Date.now() - formStartTime;
        if (timeTaken < 3000) {
            console.log('Form submitted too quickly - potential spam');
            return;
        }
        
        // Validate entire form
        const formErrors = validateForm();
        setErrors(formErrors);
        
        // Mark all fields as touched to show validation errors
        const allTouched = {};
        Object.keys(formData).forEach(field => {
            allTouched[field] = true;
        });
        setTouched(allTouched);
        
        // Check if there are any errors
        if (Object.keys(formErrors).length > 0) {
            console.log('Form has validation errors:', formErrors);
            
            // Focus on first field with error
            const firstErrorField = Object.keys(formErrors)[0];
            const errorElement = document.getElementById(firstErrorField);
            if (errorElement) {
                errorElement.focus();
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }
        
        // If validation passes, submit the form
        console.log('Form validation passed, submitting:', formData);
        setIsSubmitting(true);
          // Add metadata for backend spam detection
        const submissionData = {
            ...formData,
            _meta: {
                userAgent: navigator.userAgent,
                referrer: document.referrer,
                timestamp: Date.now(),
                timeTaken: timeTaken,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                language: navigator.language,
                platform: navigator.platform,                screenResolution: `${window.screen.width}x${window.screen.height}`,
                windowSize: `${window.innerWidth}x${window.innerHeight}`,
                // IP will be populated by backend from request headers
                clientIP: null
            }
        };
        
        axios.post('https://contact.watchelm.com', submissionData)
            .then(response => {
                console.log('Form submitted successfully:', response.data);
                setShowThankYou(true); // Show the thank you dialog
                
                // Reset form after successful submission
                setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    company: '',
                    subject: 'General Inquiry',
                    message: ''
                });
                setErrors({});
                setTouched({});
            })
            .catch(error => {
                console.error('Error submitting form:', error);
                // Show user-friendly error message
                alert('Sorry, there was an error sending your message. Please try again or contact us directly.');
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };
    
    return (
        <div>
            <h2>Start Your Journey Today.</h2>
            <p className="contact-description">Let's discuss how we can help transform your technology challenges into opportunities.</p>            <form onSubmit={handleSubmit} className="contact-form" 
                  onClick={handleFormContainerClick}
                  onMouseDown={(e) => {
                    // Set form area clicked flag immediately on mousedown
                    // This happens before blur events
                    if (e.target.closest('.contact-form')) {
                      window.__formAreaClicked = true;
                      console.log('MOUSEDOWN: Form area clicked flag set immediately');
                    }
                  }}>
                <div className="form-row">                    <div className="form-group">
                        <label htmlFor="firstName">First Name*</label>                        <input
                            ref={firstNameRef}
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChangeWithValidation}
                            onFocus={handleFocus}
                            onBlur={handleBlurWithValidation}
                            className={errors.firstName && touched.firstName ? 'error' : ''}
                            required
                        />
                        {errors.firstName && touched.firstName && (
                            <span className="error-message">{errors.firstName}</span>
                        )}
                    </div>
                    <div className="form-group">
                        <label htmlFor="lastName">Last Name*</label>                        <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChangeWithValidation}
                            onFocus={handleFocus}
                            onBlur={handleBlurWithValidation}
                            className={errors.lastName && touched.lastName ? 'error' : ''}
                            required
                        />
                        {errors.lastName && touched.lastName && (
                            <span className="error-message">{errors.lastName}</span>
                        )}
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="email">Email Address*</label>                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChangeWithValidation}
                            onFocus={handleFocus}
                            onBlur={handleBlurWithValidation}
                            className={errors.email && touched.email ? 'error' : ''}
                            required
                        />
                        {errors.email && touched.email && (
                            <span className="error-message">{errors.email}</span>
                        )}
                    </div>
                    <div className="form-group">
                        <label htmlFor="phone">Phone Number</label>                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChangeWithValidation}
                            onFocus={handleFocus}
                            onBlur={handleBlurWithValidation}
                            className={errors.phone && touched.phone ? 'error' : ''}
                        />
                        {errors.phone && touched.phone && (
                            <span className="error-message">{errors.phone}</span>
                        )}
                    </div>
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="company">Company Name</label>                        <input
                            type="text"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChangeWithValidation}
                            onFocus={handleFocus}
                            onBlur={handleBlurWithValidation}
                        />
                    </div>                    <div className="form-group">
                        <label htmlFor="subject">What's the Subject?</label>                        <select
                            id="subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChangeWithValidation}
                            onFocus={handleFocus}
                            onBlur={handleBlurWithValidation}
                        >
                            <option value="General Inquiry">General Inquiry</option>
                            <option value="Training">Training</option>
                            <option value="Consulting">Consulting</option>
                            <option value="EdTech Solutions">EdTech Solutions</option>
                        </select>
                    </div>
                </div>                <div className="form-row">
                    <div className="form-group full-width">
                        <label htmlFor="message">How Can We Help?*</label>                        <textarea
                            ref={textareaRef}
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={(e) => {
                                handleTextareaChange(e, setFormData);
                                handleChangeWithValidation(e);
                            }}
                            onFocus={handleFocus}
                            onBlur={handleBlurWithValidation}
                            className={errors.message && touched.message ? 'error' : ''}
                            rows="5"
                            required
                        ></textarea>
                        {errors.message && touched.message && (
                            <span className="error-message">{errors.message}</span>
                        )}
                    </div>
                </div>
                
                {/* Honeypot field - hidden from users but visible to bots */}
                <div className="honeypot-field" style={{ display: 'none' }}>
                    <label htmlFor="website">Website (leave blank)</label>
                    <input
                        type="text"
                        id="website"
                        name="website"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                        tabIndex="-1"
                        autoComplete="off"
                    />
                </div>
                
                <div className="form-row">
                    <button 
                        type="submit" 
                        className="submit-button"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                    </button>
                </div>
            </form>
            {showThankYou && (
                <div className="thank-you-dialog">
                    <div className="dialog-content">
                        <h3>Thank You!</h3>
                        <p>Your message has been sent successfully. We'll be in touch very soon.</p>
                        <button onClick={() => setShowThankYou(false)}>Close</button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ContactUs;